import * as apiGet from 'api/ingredient/GetIngredientIdV1';
import * as apiPost from 'api/ingredient/PostIngredientV1';
import * as apiPut from 'api/ingredient/PutIngredientIdV1';
import * as IngredientState from 'components/desktop/Ingredient/IngredientState';
import { Characteristic } from 'enums/characteristic';
import { NutriScoreCategory } from 'enums/nutriScoreCategory';
import { Optional } from 'functions/promiseExtensions';
import * as AllergenList from 'types/Ingredient/Detail/AllergenList';
import * as BaseIngredient from 'types/Ingredient/Detail/BaseIngredient';
import * as CarbonDioxideLabel from 'types/Ingredient/Detail/CarbonDioxideLabel';
import * as CarbonDioxideList from 'types/Ingredient/Detail/CarbonDioxideList';
import * as CategoryList from 'types/Ingredient/Detail/CategoryList';
import * as Image from 'types/Ingredient/Detail/Image';
import * as NutriScore from 'types/Ingredient/Detail/NutriScore';
import * as NutrientList from 'types/Ingredient/Detail/NutrientList';
import * as ProductList from 'types/Ingredient/Detail/ProductList';
import * as SeasonList from 'types/Ingredient/Detail/SeasonList';
import * as SynonymList from 'types/Ingredient/Detail/SynonymList';
import * as TagList from 'types/Ingredient/Detail/TagList';
import * as UnitWeightList from 'types/Ingredient/Detail/UnitWeightList';
import * as Translation from 'types/_general/Translation';

export type Type = {
	allergens: AllergenList.Type;
	baseIngredient: BaseIngredient.Type;
	calories: number | null;
	carbonDioxide: CarbonDioxideList.Type;
	carbonDioxideLabel: CarbonDioxideLabel.Type | null;
	carbonDioxideValue: number;
	categories: CategoryList.Type;
	characteristic: Characteristic | null;
	density: number | null;
	hasNutriScore: boolean;
	id: string;
	image: Image.Type | null;
	isAllergenOverridden: boolean;
	isCarbonDioxideOverridden: boolean;
	isScalingFactorOverridden: boolean;
	isSeasonOverridden: boolean;
	isTagOverridden: boolean;
	isUnitWeightOverridden: boolean;
	namePluralTranslations: Translation.Type<string>;
	nameSingularTranslations: Translation.Type<string>;
	nutriScore: NutriScore.Type | null;
	nutriScoreCategory: NutriScoreCategory | null;
	nutriScoreCategoryIsSetByUser: boolean;
	nutrientValues: NutrientList.Type;
	price: number | null;
	products: ProductList.Type;
	scalingFactor: number | null;
	seasons: SeasonList.Type;
	source: string | null;
	synonyms: SynonymList.Type;
	tags: TagList.Type;
	unitWeights: UnitWeightList.Type;
};

export function create(): Type {
	return {
		allergens: AllergenList.create(),
		baseIngredient: BaseIngredient.create(),
		calories: null,
		carbonDioxide: CarbonDioxideList.create(),
		carbonDioxideLabel: null,
		carbonDioxideValue: 0,
		categories: CategoryList.create(),
		characteristic: null,
		density: null,
		hasNutriScore: false,
		id: '',
		image: null,
		isAllergenOverridden: false,
		isCarbonDioxideOverridden: false,
		isScalingFactorOverridden: false,
		isSeasonOverridden: false,
		isTagOverridden: false,
		isUnitWeightOverridden: false,
		namePluralTranslations: Translation.create(),
		nameSingularTranslations: Translation.create(),
		nutriScore: null,
		nutriScoreCategory: null,
		nutriScoreCategoryIsSetByUser: false,
		nutrientValues: NutrientList.create(),
		price: null,
		products: ProductList.create(),
		scalingFactor: null,
		seasons: SeasonList.create(),
		source: null,
		synonyms: SynonymList.create(),
		tags: TagList.create(),
		unitWeights: UnitWeightList.create(),
	};
}

export async function putToApi(id: string, request: apiPut.Request) {
	return await apiPut.callApi(id, request);
}

export async function postToApi(request: apiPost.Request): Promise<string> {
	const response = await apiPost.callApi(request);
	return response.getOrDefault('');
}

export async function getFromApiOrCreate(
	id: string | undefined,
	currencyCode: string,
	copy: boolean = false
): Promise<Optional<Type>> {
	if (id) {
		const response = await apiGet.callApi(id, currencyCode);
		return response.map((e) => mapFromApi(e, copy));
	}
	return Optional.Just(create());
}

export function mapFromApi(input: apiGet.ResponseData, copy: boolean = false): Type {
	return {
		...input,
		id: copy ? '' : input.id,
		namePluralTranslations: copy
			? Translation.addCopyToEveryExistingKey(input.namePluralTranslations)
			: input.namePluralTranslations,
		nameSingularTranslations: copy
			? Translation.addCopyToEveryExistingKey(input.nameSingularTranslations)
			: input.nameSingularTranslations,
	};
}

export function mapToState(obj: Type, selectedCultureCode: string): IngredientState.Type {
	return {
		...obj,
		allergenIds: AllergenList.mapToUniqIdStringArray(obj.allergens),
		nutrients: obj.nutrientValues,
		baseIngredientId: obj.baseIngredient.id,
		baseIngredientDensity: obj.baseIngredient.density,
		baseIngredientCalories: obj.baseIngredient.calories,
		baseIngredientCharacteristic: obj.baseIngredient.characteristic,
		baseIngredientNutrients: obj.baseIngredient.nutrientValues,
		baseIngredientScalingFactor: obj.baseIngredient.scalingFactor,
		categoryIds: obj.categories.map((e) => e.id),
		characteristic: obj.characteristic,
		co2Footprints: obj.carbonDioxide,
		locked: {
			scalingFactor: !obj.isScalingFactorOverridden,
			calories: obj.calories === null,
			characteristic: obj.characteristic === null,
			density: obj.density === null,
			unitWeights: !obj.isUnitWeightOverridden,
			tags: !obj.isTagOverridden,
			allergen: !obj.isAllergenOverridden,
			season: !obj.isSeasonOverridden,
			co2: !obj.isCarbonDioxideOverridden,
		},
		namePlural: obj.namePluralTranslations,
		nameSingular: obj.nameSingularTranslations,
		products: obj.products,
		seasonIds: obj.seasons.map((e) => e.id),
		selectedCultureCode: selectedCultureCode,
		tagIds: obj.tags.map((e) => e.id),
	};
}
